import { useEffect, useState } from "react";
import useFetch from "../Utility/useFetch";
import IndividualIngredient from "./IndividualIngredient";
import { buildApiUrl } from "../Utility/api";

const SHOPPING_LIST_DATE_STORAGE_KEY = "shoppingListDate";
const SHOPPING_FILTER_STORAGE_KEY = "ingredientFilterList";
const CUSTOM_INGREDIENTS_STORAGE_KEY = "customShoppingIngredients";

const loadSavedCustomIngredients = (shoppingListDate) => {
  if (!shoppingListDate) {
    return [];
  }

  const savedCustomIngredients = JSON.parse(
    localStorage.getItem(CUSTOM_INGREDIENTS_STORAGE_KEY) || "{}"
  );

  return Array.isArray(savedCustomIngredients[shoppingListDate])
    ? savedCustomIngredients[shoppingListDate]
    : [];
};

const saveCustomIngredients = (shoppingListDate, nextIngredients) => {
  if (!shoppingListDate) {
    return;
  }

  const savedCustomIngredients = JSON.parse(
    localStorage.getItem(CUSTOM_INGREDIENTS_STORAGE_KEY) || "{}"
  );

  if (nextIngredients.length > 0) {
    savedCustomIngredients[shoppingListDate] = nextIngredients;
  } else {
    delete savedCustomIngredients[shoppingListDate];
  }

  localStorage.setItem(
    CUSTOM_INGREDIENTS_STORAGE_KEY,
    JSON.stringify(savedCustomIngredients)
  );
};

const ShoppingList = () => {
  const [selecetedOption, setSelecetedOption] = useState("");
  const [selecetedIngredients, setSelecetedIngredients] = useState([]);
  const [filterList, setFilterList] = useState([]);
  const [customIngredientInput, setCustomIngredientInput] = useState("");
  const [customIngredients, setCustomIngredients] = useState([]);
  const [showCustomIngredientInput, setShowCustomIngredientInput] = useState(false);
  const { data } = useFetch(
    {url: buildApiUrl("/food/shoppingserver"), skip: false}
  );
  const resetValues = () => {
    setFilterList([]);
    localStorage.setItem(SHOPPING_FILTER_STORAGE_KEY, JSON.stringify([]));
  };
  const changeFilterList = (val) => {
    var copiedList = [...filterList];
    var index = filterList.indexOf(val);
    if (index !== -1) {
      copiedList.splice(index, 1);
    } else {
      copiedList = [...copiedList, val];
    }
    setFilterList(copiedList);
    localStorage.setItem(SHOPPING_FILTER_STORAGE_KEY, JSON.stringify(copiedList));
  };

  const addCustomIngredients = () => {
    const parsedIngredients = customIngredientInput
      .split(/\r?\n/)
      .map((ingredient) => ingredient.replace(/^\s*[-*•]\s*/, "").trim())
      .filter(Boolean);

    if (parsedIngredients.length === 0) {
      return;
    }

    const nextCustomIngredients = [...customIngredients, ...parsedIngredients];
    setCustomIngredients(nextCustomIngredients);
    saveCustomIngredients(selecetedOption, nextCustomIngredients);
    setCustomIngredientInput("");
    setShowCustomIngredientInput(false);
  };

  const cancelCustomIngredients = () => {
    setCustomIngredientInput("");
    setShowCustomIngredientInput(false);
  };

  useEffect(() => {
    if (!data || data.length === 0) {
      return;
    }

    const savedDate = localStorage.getItem(SHOPPING_LIST_DATE_STORAGE_KEY);
    const fallbackDate =
      (savedDate &&
        data.find((shoppingItem) => shoppingItem.createddate === savedDate)
          ?.createddate) ||
      data[0].createddate;
    const nextSelectedOption = selecetedOption || fallbackDate;

    if (nextSelectedOption !== selecetedOption) {
      setFilterList(
        JSON.parse(localStorage.getItem(SHOPPING_FILTER_STORAGE_KEY)) || []
      );
      setSelecetedOption(nextSelectedOption);
      return;
    }

    let correctIngredients = data.find(
      (shoppingItem) => shoppingItem.createddate === nextSelectedOption
    );

    if (!correctIngredients) {
      return;
    }

    setSelecetedIngredients(correctIngredients.shoppinglist.split("\n"));
    setCustomIngredients(loadSavedCustomIngredients(nextSelectedOption));

    if (localStorage.getItem(SHOPPING_LIST_DATE_STORAGE_KEY) !== nextSelectedOption) {
      setFilterList([]);
      localStorage.setItem(SHOPPING_FILTER_STORAGE_KEY, JSON.stringify([]));
    }

    localStorage.setItem(SHOPPING_LIST_DATE_STORAGE_KEY, nextSelectedOption);
  }, [selecetedOption, data]);
  return (
    <div className="shoppingListDiv">
      <div className="shoppingListHeader">
        <div>
          <label>Saved Lists</label>
          <select
            type="Select"
            className="inputBox"
            value={selecetedOption}
            onChange={(e) => {
              setSelecetedOption(e.target.value);
            }}
          >
            {data &&
              data.map((shoppingResp) => {
                return (
                  <option key={shoppingResp.createddate}>
                    {shoppingResp.createddate}
                  </option>
                );
              })}
          </select>
        </div>
        <button className="summaryButton" onClick={resetValues}>
          Reset
        </button>
        <button
          className="summaryButton shoppingListActionButton"
          onClick={() => {
            setShowCustomIngredientInput(!showCustomIngredientInput);
          }}
        >
          Add Custom
        </button>
      </div>
      {showCustomIngredientInput && (
        <div className="shoppingListCustomInput">
          <label htmlFor="custom-shopping-items">Add Custom Items</label>
          <textarea
            id="custom-shopping-items"
            className="textAreaAdd ShoppingListPasteInput"
            placeholder="Paste one item per line"
            value={customIngredientInput}
            onChange={(e) => {
              setCustomIngredientInput(e.target.value);
            }}
          />
          <div className="shoppingListActionRow">
            <button
              className="summaryButton shoppingListActionButton"
              onClick={cancelCustomIngredients}
            >
              Cancel
            </button>
            <button
              className="summaryButton shoppingListActionButton"
              onClick={addCustomIngredients}
              disabled={!customIngredientInput.trim()}
            >
              Save
            </button>
          </div>
        </div>
      )}
      {selecetedIngredients &&
        selecetedIngredients.map((ingredient, index) => {
          if (ingredient) {
            return (
              <IndividualIngredient
                index={index}
                ingredient={ingredient}
                key={ingredient + index}
                isDefaultFilter={filterList.includes(index)}
                changeFilterList={changeFilterList}
              />
            );
          }

          return null;
        })}
      {customIngredients.map((ingredient, index) => {
        return (
          <IndividualIngredient
            index={selecetedIngredients.length + index}
            ingredient={ingredient}
            key={ingredient + selecetedIngredients.length + index}
            isDefaultFilter={filterList.includes(selecetedIngredients.length + index)}
            changeFilterList={changeFilterList}
          />
        );
      })}
    </div>
  );
};
export default ShoppingList;
